<template>
  <v-menu
    v-model="isOpen"
    :target="menuTarget"
    location="bottom"
    :close-on-content-click="false"
    max-width="400"
    offset="8"
  >
    <v-card v-if="contact" class="contact-popup" elevation="8">
      <!-- Header -->
      <v-card-title
        class="d-flex flex-column align-start pa-3 bg-green-lighten-5"
      >
        <div class="d-flex align-center w-100">
          <v-icon color="green-darken-2" class="mr-2" size="small"
            >mdi-account</v-icon
          >
          <span class="text-subtitle-1 font-weight-medium">{{
            contact.name
          }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="x-small"
            @click="closePopup"
          ></v-btn>
        </div>
        <div
          v-if="hierarchyTrail"
          class="text-caption text-medium-emphasis ml-7 mt-1"
        >
          {{ hierarchyTrail }}
        </div>
      </v-card-title>

      <!-- Content -->
      <v-card-text class="pa-3">
        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-center align-center py-4">
          <v-progress-circular
            indeterminate
            color="green-darken-2"
            size="32"
          ></v-progress-circular>
        </div>

        <!-- Contact details -->
        <div v-else class="contact-details">
          <!-- Hierarchy (if not top node) -->
          <div v-if="contact.parent_name" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-sitemap</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">Hiërarchie</div>
              <div class="text-body-2">{{ contact.parent_name }}</div>
            </div>
          </div>

          <!-- Contact type -->
          <div v-if="contact.display_name" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-tag</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">Type</div>
              <div class="text-body-2">
                {{ formatContactType(contact.type) }}
              </div>
            </div>
          </div>

          <!-- Contact categories -->
          <div
            v-if="
              contact.contact_categories &&
              contact.contact_categories.length > 0
            "
            class="detail-row"
          >
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-tag-multiple</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">Categorieën</div>
              <div class="d-flex flex-wrap ga-1 mt-1">
                <v-chip
                  v-for="category in contact.contact_categories"
                  :key="category.id"
                  :color="category.color || 'grey'"
                  size="x-small"
                  variant="tonal"
                >
                  <v-icon
                    :icon="category.icon || 'mdi-folder'"
                    start
                    size="12"
                  />
                  {{ category.name }}
                </v-chip>
              </div>
            </div>
          </div>

          <!-- Primary Phone -->
          <div v-if="contact.primary_phone_formatted" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-phone</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">
                Telefoon
                <span
                  v-if="contact.primary_phone_type"
                  class="text-caption text-medium-emphasis"
                >
                  ({{ contact.primary_phone_type }})
                </span>
              </div>
              <a
                :href="`tel:${contact.primary_phone_formatted}`"
                class="text-body-2 text-decoration-none contact-link"
                @click.stop
              >
                {{ contact.primary_phone_formatted }}
              </a>
            </div>
          </div>

          <!-- Primary Email -->
          <div v-if="contact.primary_email_address" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-email</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">E-mail</div>
              <a
                :href="`mailto:${contact.primary_email_address}`"
                class="text-body-2 text-decoration-none contact-link"
                @click.stop
              >
                {{ contact.primary_email_address }}
              </a>
            </div>
          </div>

          <!-- Primary Address -->
          <div v-if="contact.primary_location_address" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-map-marker</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">Adres</div>
              <div class="text-body-2">
                {{ contact.primary_location_address }}
              </div>
            </div>
          </div>

          <!-- First note (if any) -->
          <div v-if="firstNote" class="detail-row">
            <v-icon size="x-small" class="mr-2 text-medium-emphasis"
              >mdi-note-text</v-icon
            >
            <div class="detail-content">
              <div class="text-caption text-medium-emphasis">
                Notitie
                <span v-if="contact.notes.length > 1" class="ml-1">
                  (+{{ contact.notes.length - 1 }} meer)
                </span>
              </div>
              <div class="text-body-2 note-preview">
                {{ truncateNote(firstNote.note) }}
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div
            v-if="!hasAnyDetails"
            class="text-center py-3 text-medium-emphasis"
          >
            <v-icon size="small" color="grey-lighten-1" class="mb-1"
              >mdi-information-outline</v-icon
            >
            <div class="text-caption">Geen aanvullende informatie</div>
          </div>
        </div>
      </v-card-text>

      <!-- Actions -->
      <v-divider></v-divider>
      <v-card-actions class="pa-2">
        <v-spacer></v-spacer>
        <v-btn
          color="green-darken-2"
          variant="text"
          size="small"
          prepend-icon="mdi-open-in-new"
          @click="navigateToContact"
        >
          Details
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { useContactsStoreRefactor } from "@/stores/contactsStoreRefactor";

export default {
  name: "ContactInfoPopup",
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: [Number, String],
      default: null,
    },
    menuTarget: {
      type: [String, Object],
      default: undefined,
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const router = useRouter();
    const contactsStore = useContactsStoreRefactor();
    const loading = ref(false);
    const contact = ref(null);

    const isOpen = computed({
      get: () => props.modelValue,
      set: (val) => emit("update:modelValue", val),
    });

    const hasAnyDetails = computed(() => {
      if (!contact.value) return false;
      return (
        contact.value.parent_name ||
        contact.value.primary_phone_formatted ||
        contact.value.primary_email_address ||
        contact.value.primary_location_address ||
        (contact.value.notes && contact.value.notes.length > 0) ||
        (contact.value.contact_categories &&
          contact.value.contact_categories.length > 0)
      );
    });

    const firstNote = computed(() => {
      if (!contact.value?.notes || contact.value.notes.length === 0)
        return null;
      return contact.value.notes[0];
    });

    // Compute hierarchy trail (Company > Department for individuals)
    const hierarchyTrail = computed(() => {
      if (!contact.value || !contact.value.type) return null;

      const type = String(contact.value.type).toLowerCase();

      // For individuals: show "Company > Department" or just "Department/Company"
      if (type.includes("individual")) {
        if (!contact.value.parent_name) return null;

        // Try to find the parent contact from store to check if it's a department
        const parent = contactsStore.contacts.find(
          (c) => c.name === contact.value.parent_name,
        );

        if (
          parent &&
          parent.type &&
          parent.type.toLowerCase().includes("department")
        ) {
          // Parent is a department, check if it has a parent (company)
          if (parent.parent_name) {
            return `${parent.parent_name} > ${contact.value.parent_name}`;
          }
        }

        // Parent is a company or not found - just show parent name
        return contact.value.parent_name;
      }

      // For departments: show "Company"
      if (type.includes("department")) {
        return contact.value.parent_name || null;
      }

      // For organizations: no trail
      return null;
    });

    // Watch for contactId changes and fetch contact data
    watch(
      () => props.contactId,
      async (newId) => {
        if (newId && props.modelValue) {
          await fetchContact(newId);
        }
      },
      { immediate: true },
    );

    // Watch for dialog opening
    watch(
      () => props.modelValue,
      async (isOpening) => {
        if (isOpening && props.contactId) {
          await fetchContact(props.contactId);
        } else if (!isOpening) {
          // Clear contact when closing (optional)
          // contact.value = null;
        }
      },
    );

    const fetchContact = async (contactId) => {
      if (!contactId) return;

      loading.value = true;
      try {
        // Try to get from store first
        const cachedContact = contactsStore.contacts.find(
          (c) => c.id == contactId,
        );

        if (cachedContact) {
          contact.value = cachedContact;
        } else {
          // Fetch from API
          contact.value = await contactsStore.fetchById(contactId);
        }
      } catch (error) {
        console.error("Error fetching contact:", error);
        contact.value = null;
      } finally {
        loading.value = false;
      }
    };

    const formatContactType = (type) => {
      if (!type) return "";
      // Convert "Contact::Person" to "Person", etc.
      const parts = type.split("::");
      return parts[parts.length - 1] || type;
    };

    const truncateNote = (content) => {
      if (!content) return "";
      const maxLength = 100;
      if (content.length <= maxLength) return content;
      return content.substring(0, maxLength) + "...";
    };

    const closePopup = () => {
      isOpen.value = false;
    };

    const navigateToContact = () => {
      if (contact.value?.id) {
        router.push(`/contacts/${contact.value.id}`);
        closePopup();
      }
    };

    return {
      isOpen,
      loading,
      contact,
      hasAnyDetails,
      firstNote,
      hierarchyTrail,
      closePopup,
      navigateToContact,
      formatContactType,
      truncateNote,
    };
  },
};
</script>

<style scoped>
.contact-popup {
  border-top: 3px solid #2e7d32;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.detail-content {
  flex: 1;
  min-width: 0;
}

.contact-link {
  color: #2e7d32 !important;
  word-break: break-all;
}

.contact-link:hover {
  text-decoration: underline !important;
}

.note-preview {
  white-space: pre-line;
  word-break: break-word;
  line-height: 1.4;
}
</style>
